import _cloneDeep from 'lodash/cloneDeep';
import { alertTriggerDescription } from 'services/utils';
import { buildContactGroupInfo } from './contact-group-utils';
import { DEFAULT_TOKEN, getPageSize, persistPageSize } from 'services/PaginationHelper';
import { STARTER_TEMPLATES } from './starter-templates';
import { projectMatchesLabels } from './template-projects-utils';
import { isPetGlycolFilterAvailable } from 'services/charting/constants';

// Import validation modal components
const validationModalTemplate = require('./validation-modal/validation-modal.html');
const ValidationModalController = require('./validation-modal/validation-modal.controller').default;

// Import bulk edit modal components
const bulkEditModalTemplate = require('./bulk-edit-templates-modal/bulk-edit-templates-modal.html');
const BulkEditTemplatesModalController = require('./bulk-edit-templates-modal/bulk-edit-templates-modal.controller').default;

const PAGE_SIZE_SUFFIX = 'alert-templates';

// Webhook, Corrigo, Service Channel and Jolt List are configured with a URL or
// IDs instead, so they have no recipients and cannot be missing any.
const RECIPIENT_ACTION_TYPES = ['EMAIL', 'SMS', 'PHONE_CALL'];

export default class AlertTemplatesController {

    /* @ngInject */
    constructor($scope, DialogService, RulesApiService, ProjectManager, ToastService, SonnerToastService, IAMService, FeatureFlags) {
        this.$scope = $scope;
        this.DialogService = DialogService;
        this.RulesApiService = RulesApiService;
        this.ProjectManager = ProjectManager;
        this.ToastService = ToastService;
        this.SonnerToastService = SonnerToastService;
        this.IAMService = IAMService;
        this.FeatureFlags = FeatureFlags;
    }

    showBulkGlycolFilterOption() {
        return isPetGlycolFilterAvailable(this.FeatureFlags);
    }

    $onInit() {
        this.loadingTemplates = false;
        this.allTemplates = []; // All templates from API (no server-side pagination)
        this.templates = []; // Current page slice
        this.currentPageSize = getPageSize(PAGE_SIZE_SUFFIX);
        this.currentPageToken = DEFAULT_TOKEN;
        this.nextPageToken = '';
        this.selectedTemplate = null;
        this.isNewTemplate = false;
        this.escalationLevelsCache = new Map(); // Cache for escalation levels
        this.projectCountsCache = new Map(); // Cache for project counts
        this.allProjects = []; // Store all projects for counting
        this.contactGroupInfo = new Map(); // Cache for contact group contact info
        this.loadingContactGroupInfo = false;
        this.selectedTemplateNames = new Set(); // Track selected templates for bulk edit
        this.starterTemplates = STARTER_TEMPLATES;
        this.searchQuery = '';

        // Load templates on initialization
        this.loadTemplates();
    }

    async loadTemplates() {
        this.loadingTemplates = true;

        try {
            const organizationId = this.ProjectManager.currentProject.organizationId;

            // API returns all templates at once (no server-side pagination)
            const response = await this.RulesApiService.organizationRules(organizationId, {});
            this.allTemplates = response.data || [];

            // Convert API format to frontend format
            this.allTemplates.forEach(template => {
                this.convertTemplateFromApiFormat(template);
            });

            // Sort once so pagination boundaries are alphabetical
            this.allTemplates.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));

            this.applyClientPagination();

            // Clear cache when templates are reloaded
            this.escalationLevelsCache.clear();
            this.projectCountsCache.clear();

            // Deep-linked from elsewhere (e.g. an inherited alert rule's "Go to Organization
            // Template" button) with a specific template to open. Only honor it once, so a
            // later reload (e.g. after a bulk edit) doesn't keep forcing this template back open.
            if (this.selectedTemplateName) {
                const template = this.allTemplates.find(t => t.name === this.selectedTemplateName);
                this.selectedTemplateName = null;
                if (template) {
                    this.editTemplate(template);
                }
            }

        } catch (error) {
            console.error('Failed to load alert templates:', error); // eslint-disable-line no-console
            const errorMessage = error.data?.error || error.message || 'Unknown error';
            this.ToastService.showSimpleText(`Failed to load alert templates: ${errorMessage}`);
            this.allTemplates = [];
            this.templates = [];
        } finally {
            this.loadingTemplates = false;
            this.$scope.$applyAsync();
        }

        // Load project counts and contact group warnings in parallel, after templates are rendered.
        // Isolated so failures don't affect template display.
        this.loadAllProjectsAndCalculateCounts()
            .then(() => this.loadContactGroupWarnings())
            .catch(error => {
                console.error('Failed to load contact group warnings:', error); // eslint-disable-line no-console
            });
    }

    getFilteredTemplates() {
        if (!this.searchQuery || !this.searchQuery.trim()) {
            return this.allTemplates;
        }
        const query = this.searchQuery.trim().toLowerCase();
        const queryWords = query.split(/\s+/).filter(w => w.length > 0);
        return this.allTemplates.filter(template => {
            const name = (template.displayName || '').toLowerCase();
            // Each word in the query must appear as a substring in the name
            return queryWords.every(word => name.includes(word));
        });
    }

    onSearchQueryChanged() {
        this.currentPageToken = DEFAULT_TOKEN;
        this.applyClientPagination();
    }

    clearSearchQuery() {
        this.searchQuery = '';
        this.currentPageToken = DEFAULT_TOKEN;
        this.applyClientPagination();
    }

    applyClientPagination() {
        const filtered = this.getFilteredTemplates();
        const offset = parseInt(this.currentPageToken, 10) || 0;
        this.totalTemplatesCount = filtered.length;
        this.templates = filtered.slice(offset, offset + this.currentPageSize);

        // Set next page token as the next offset, or empty if we're on the last page
        const nextOffset = offset + this.currentPageSize;
        this.nextPageToken = nextOffset < filtered.length ? nextOffset.toString() : '';
    }

    convertTemplateFromApiFormat(template) {
        // Convert projectLabels from API format to frontend scope format
        if (!template.scope) {
            template.scope = {
                type: 'ALL_PROJECTS',
                labels: {}
            };

            // Check if there are project labels
            if (template.projectLabels && Array.isArray(template.projectLabels) && template.projectLabels.length > 0) {
                template.scope.type = 'PROJECT_LABELS';
                template.scope.labels = {};
                template.projectLabels.forEach(label => {
                    if (label.key) {
                        template.scope.labels[label.key] = label.value || '';
                    }
                });
            } else if (template.projectLabels && typeof template.projectLabels === 'object' && Object.keys(template.projectLabels).length > 0) {
                template.scope.type = 'PROJECT_LABELS';
                template.scope.labels = template.projectLabels;
            }
        }

        // Convert deviceLabels to deviceMode
        if (!template.deviceMode) {
            if (template.deviceLabels && Object.keys(template.deviceLabels).length > 0) {
                template.deviceMode = 'LABELS';
            } else {
                template.deviceMode = 'ALL';
            }
        }

        // Convert escalationPath from API to escalationTemplateId for frontend
        if (template.escalationPath) {
            template.escalationTemplateId = template.escalationPath;
            delete template.escalationPath;
        }

        // Handle schedule format - no conversion needed as API and frontend use same format
        // Schedule is already in the correct format with time objects {hour: X, minute: Y}
        // The conversion to string format happens in the template-detail controller
    }

    get isDetailView() {
        return this.selectedTemplate !== null;
    }

    isActive(templateName) {
        return this.selectedTemplate && this.selectedTemplate.name === templateName;
    }

    createNewTemplate() {
        this.selectedTemplate = {
            displayName: '',
            enabled: true,
            trigger: {
                field: null
            },
            scope: {
                type: 'ALL_PROJECTS',
                labels: {}
            },
            deviceMode: null,
            deviceLabels: {},
            escalationLevels: [],
            actions: []
        };
        this.isNewTemplate = true;
    }

    createFromStarter(starter) {
        this.selectedTemplate = _cloneDeep(starter.template);
        this.isNewTemplate = true;
    }

    editTemplate(template) {
        this.clearSelection();
        this.selectedTemplate = _cloneDeep(template);
        this.isNewTemplate = false;
    }

    closeDetailView() {
        this.selectedTemplate = null;
        this.isNewTemplate = false;
    }

    // Bulk selection methods
    toggleTemplateSelection(template, $event) {
        $event.stopPropagation();
        if (this.selectedTemplateNames.has(template.name)) {
            this.selectedTemplateNames.delete(template.name);
        } else {
            this.selectedTemplateNames.add(template.name);
        }
    }

    toggleSelectAll() {
        if (this.isAllOnPageSelected()) {
            this.selectedTemplateNames.clear();
        } else {
            this.templates.forEach(t => this.selectedTemplateNames.add(t.name));
        }
    }

    selectAllTemplates() {
        this.allTemplates.forEach(t => this.selectedTemplateNames.add(t.name));
    }

    isTemplateSelected(template) {
        return this.selectedTemplateNames.has(template.name);
    }

    isAllOnPageSelected() {
        return this.templates.length > 0 && this.templates.every(t => this.selectedTemplateNames.has(t.name));
    }

    isAllTemplatesSelected() {
        return this.allTemplates.length > 0 && this.selectedTemplateNames.size === this.allTemplates.length;
    }

    hasMoreTemplatesThanCurrentPage() {
        return this.allTemplates.length > this.templates.length;
    }

    getSelectedCount() {
        return this.selectedTemplateNames.size;
    }

    getSelectedTemplates() {
        return this.allTemplates.filter(t => this.selectedTemplateNames.has(t.name));
    }

    clearSelection() {
        this.selectedTemplateNames.clear();
    }

    showBulkEditModal(mode) {
        const selectedTemplates = this.getSelectedTemplates();
        if (selectedTemplates.length === 0) return;

        this.DialogService.show({
            template: bulkEditModalTemplate,
            controller: BulkEditTemplatesModalController,
            controllerAs: 'ctrl',
            parent: document.body,
            clickOutsideToClose: false,
            escapeToClose: true,
            fullscreen: false,
            locals: {
                mode: mode,
                selectedTemplates: _cloneDeep(selectedTemplates),
                allProjects: this.allProjects || [],
                RulesApiService: this.RulesApiService
            }
        }).then((results) => {
            if (results && results.succeeded > 0) {
                this.SonnerToastService.success(
                    this.getBulkEditTitle(mode, results.succeeded),
                    this.getBulkEditDescription(mode, results.succeeded)
                );
            }
            if (results && results.failed > 0) {
                this.SonnerToastService.error(
                    `${results.failed} template${results.failed !== 1 ? 's' : ''} failed to update`,
                    results.errors?.[0]?.message || 'An unexpected error occurred'
                );
            }
            this.clearSelection();
            this.loadTemplates();
        }).catch(() => {
            // Dialog cancelled
        });
    }

    getBulkEditTitle(mode, count) {
        const plural = count !== 1 ? 's' : '';
        switch (mode) {
            case 'activateDeactivate': return `Updated ${count} template${plural}`;
            case 'acknowledgement': return `Acknowledgment duration updated`;
            case 'resolvedNotifications': return `Resolved notifications updated`;
            case 'glycolFilter': return `Glycol filter updated`;
            case 'schedule': return `Schedule updated`;
            default: return `Updated ${count} template${plural}`;
        }
    }

    getBulkEditDescription(mode, count) {
        const plural = count !== 1 ? 's' : '';
        switch (mode) {
            case 'activateDeactivate': return `Enabled/disabled setting applied to ${count} template${plural}`;
            case 'acknowledgement': return `New acknowledgment duration applied to ${count} template${plural}`;
            case 'resolvedNotifications': return `Resolved notification setting changed on ${count} template${plural}`;
            case 'glycolFilter': return `Virtual glycol filter toggled on ${count} template${plural}`;
            case 'schedule': return `Schedule applied to ${count} template${plural}`;
            default: return `Changes applied to ${count} template${plural}`;
        }
    }

    templateTriggerDescription(template) {
        return alertTriggerDescription(template);
    }

    async onTemplateCreated(template) {
        try {
            const organizationId = this.ProjectManager.currentProject.organizationId;

            const response = await this.RulesApiService.createOrganizationRule(organizationId, template);

            // Convert API format to frontend format
            this.convertTemplateFromApiFormat(response);

            // Add the new template to the source list and re-paginate
            this.allTemplates.push(response);
            this.allTemplates.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
            this.applyClientPagination();
            this.ToastService.showSimpleText('Alert template created successfully');

            // Clear cache since templates have changed
            this.escalationLevelsCache.clear();
            this.projectCountsCache.clear();

            // Calculate project count for the new template if it has PROJECT_LABELS scope
            if (response.scope && response.scope.type === 'PROJECT_LABELS') {
                this.calculateProjectCount(response);
            }

            // Refresh contact group warnings for new contact groups
            this.loadContactGroupWarnings();

            this.closeDetailView();
            this.$scope.$applyAsync();
            return response;

        } catch (error) {
            console.error('Failed to create alert template:', error); // eslint-disable-line no-console
            const errorMessage = error.data?.error || error.message || 'Unknown error';
            this.ToastService.showSimpleText(`Failed to create alert template: ${errorMessage}`);
            this.closeDetailView();
            this.$scope.$applyAsync();
            throw error;
        }
    }

    async onTemplateUpdated(updatedTemplate) {
        try {
            const response = await this.RulesApiService.updateOrganizationRule(updatedTemplate);

            // Convert API format to frontend format
            this.convertTemplateFromApiFormat(response);

            // Update the template in the source list and re-paginate
            const index = this.allTemplates.findIndex(t => t.name === updatedTemplate.name);
            if (index !== -1) {
                this.allTemplates[index] = response;
            }
            this.applyClientPagination();
            this.ToastService.showSimpleText('Alert template updated successfully');

            // Clear cache since templates have changed
            this.escalationLevelsCache.clear();
            this.projectCountsCache.clear();

            // Calculate project count for the updated template if it has PROJECT_LABELS scope
            if (response.scope && response.scope.type === 'PROJECT_LABELS') {
                this.calculateProjectCount(response);
            }

            // Refresh contact group warnings for updated contact groups
            await this.loadContactGroupWarnings();

            this.closeDetailView();
            this.$scope.$applyAsync();
            return response;

        } catch (error) {
            console.error('Failed to update alert template:', error); // eslint-disable-line no-console
            const errorMessage = error.data?.error || error.message || 'Unknown error';
            this.ToastService.showSimpleText(`Failed to update alert template: ${errorMessage}`);
            this.closeDetailView();
            this.$scope.$applyAsync();
            throw error;
        }
    }

    async onTemplateDeleted(templateName) {
        try {
            await this.RulesApiService.deleteOrganizationRule(templateName);

            // Remove the template from the source list and re-paginate
            this.allTemplates = this.allTemplates.filter(t => t.name !== templateName);
            this.applyClientPagination();
            this.ToastService.showSimpleText('Alert template deleted successfully');

            // Clear cache since templates have changed
            this.escalationLevelsCache.clear();
            this.projectCountsCache.clear();

            this.closeDetailView();
            this.$scope.$applyAsync();

        } catch (error) {
            console.error('Failed to delete alert template:', error); // eslint-disable-line no-console
            const errorMessage = error.data?.error || error.message || 'Unknown error';
            this.ToastService.showSimpleText(`Failed to delete alert template: ${errorMessage}`);
            this.closeDetailView();
            this.$scope.$applyAsync();
            throw error;
        }
    }

    showDeleteConfirmation({ template }) {
        const confirmMessage = `Delete template "${template.displayName}"?`;
        const additionalMessage = 'Projects using this template will no longer receive these alerts.';

        this.DialogService.confirm({
            title: confirmMessage,
            textContent: additionalMessage,
            ok: 'Delete',
            cancel: 'Cancel',
            maxWidth: '500px',
        }).then(() => {
            this.deleteTemplate(template);
        });
    }

    deleteTemplate(template) {
        this.onTemplateDeleted(template.name);
    }

    duplicateTemplate(template) {
        // Create a deep copy of the template
        const duplicatedTemplate = _cloneDeep(template);

        // Modify the duplicated template
        duplicatedTemplate.displayName = `${template.displayName} Copy`;
        duplicatedTemplate.enabled = false; // Deactivate the copy

        // Remove properties that should not be copied
        delete duplicatedTemplate.name; // API will generate a new name
        delete duplicatedTemplate.matchingProjectsCount; // Will be recalculated
        delete duplicatedTemplate.totalProjectsCount; // Will be recalculated

        // Ensure proper structure for duplicated template (matching createNewTemplate defaults)
        if (!duplicatedTemplate.trigger) {
            duplicatedTemplate.trigger = { field: null };
        }
        if (!duplicatedTemplate.scope) {
            duplicatedTemplate.scope = { type: 'ALL_PROJECTS', labels: {} };
        }
        if (!duplicatedTemplate.deviceMode) {
            duplicatedTemplate.deviceMode = 'ALL';
        }
        if (!duplicatedTemplate.deviceLabels) {
            duplicatedTemplate.deviceLabels = {};
        }
        if (!duplicatedTemplate.escalationLevels) {
            duplicatedTemplate.escalationLevels = [];
        }
        if (!duplicatedTemplate.actions) {
            duplicatedTemplate.actions = [];
        }

        // Apply the same API-to-frontend conversions that would normally happen for existing templates
        // This is needed because duplicated templates are marked as "new" but contain API-formatted data
        if (duplicatedTemplate.trigger?.range?.type === 'OUTSIDE') {
            if (duplicatedTemplate.trigger.range.lower !== null && duplicatedTemplate.trigger.range.upper === null) {
                duplicatedTemplate.trigger.range.type = 'BELOW';
            }
            if (duplicatedTemplate.trigger.range.lower === null && duplicatedTemplate.trigger.range.upper !== null) {
                duplicatedTemplate.trigger.range.type = 'ABOVE';
            }
        }

        // Set up the template for editing
        this.selectedTemplate = duplicatedTemplate;
        this.isNewTemplate = true;
    }

    setPageSize({ pageSize }) {
        persistPageSize(pageSize, PAGE_SIZE_SUFFIX);
        this.currentPageSize = pageSize;
        this.currentPageToken = DEFAULT_TOKEN; // Reset to first page
        this.clearSelection();
        this.applyClientPagination();
        this.$scope.$applyAsync();
    }

    setPageToken({ pageToken }) {
        this.currentPageToken = pageToken;
        this.clearSelection();
        this.applyClientPagination();
        this.$scope.$applyAsync();
    }

    getScopeLabelCount(template) {
        if (!template.scope || !template.scope.labels) {
            return 0;
        }
        return Object.keys(template.scope.labels).length;
    }

    getTotalRecipientsCount(action) {
        const actionObject = action[action.type.toLowerCase()];
        if (!actionObject) return 0;

        const recipientsCount = actionObject.recipients ? actionObject.recipients.length : 0;
        const contactGroupsCount = actionObject.contactGroups ? actionObject.contactGroups.length : 0;

        return recipientsCount + contactGroupsCount;
    }

    getContactGroupDisplayName(resourceName) {
        const contactGroup = this.contactGroups ? this.contactGroups.find(cg => cg.name === resourceName) : null;
        return contactGroup ? contactGroup.displayName : resourceName;
    }

    // Resolve escalation levels from a referenced escalation template or from the template's own inline levels
    getResolvedEscalationLevels(template) {
        if (template.escalationTemplateId) {
            const tmpl = (this.escalationTemplates || []).find(t => t.name === template.escalationTemplateId);
            if (tmpl) return tmpl.escalationLevels || [];
        }
        return template.escalationLevels || [];
    }

    getAllContactGroups(template) {
        const contactGroups = new Set();

        const escalationLevels = this.getResolvedEscalationLevels(template);
        if (!Array.isArray(escalationLevels)) {
            return [];
        }

        escalationLevels.forEach(level => {
            if (level.actions && Array.isArray(level.actions)) {
                level.actions.forEach(action => {
                    let actionContactGroups = [];

                    // Handle different action types
                    if (action.type === 'EMAIL' || action.type === 'SMS') {
                        const actionObject = action[action.type.toLowerCase()];
                        if (actionObject && actionObject.contactGroups) {
                            actionContactGroups = actionObject.contactGroups;
                        }
                    } else if (action.type === 'PHONE_CALL') {
                        if (action.phoneCall && action.phoneCall.contactGroups) {
                            actionContactGroups = action.phoneCall.contactGroups;
                        }
                    }

                    // Add contact groups to the set (automatically handles duplicates)
                    actionContactGroups.forEach(groupName => {
                        if (groupName) {
                            contactGroups.add(groupName);
                        }
                    });
                });
            }
        });

        return Array.from(contactGroups);
    }

    getAllDirectRecipients(template) {
        const recipients = new Set();

        const escalationLevels = this.getResolvedEscalationLevels(template);
        if (!Array.isArray(escalationLevels)) {
            return [];
        }

        escalationLevels.forEach(level => {
            if (level.actions && Array.isArray(level.actions)) {
                level.actions.forEach(action => {
                    let actionRecipients = [];

                    if (action.type === 'EMAIL' || action.type === 'SMS') {
                        const actionObject = action[action.type.toLowerCase()];
                        if (actionObject && actionObject.recipients) {
                            actionRecipients = actionObject.recipients;
                        }
                    } else if (action.type === 'PHONE_CALL') {
                        if (action.phoneCall && action.phoneCall.recipients) {
                            actionRecipients = action.phoneCall.recipients;
                        }
                    }

                    actionRecipients.forEach(recipient => {
                        if (recipient) {
                            recipients.add(recipient);
                        }
                    });
                });
            }
        });

        return Array.from(recipients);
    }

    getAllActions(template) {
        const escalationLevels = this.getResolvedEscalationLevels(template);
        if (!Array.isArray(escalationLevels)) {
            return [];
        }

        return escalationLevels.flatMap(level => level.actions || []);
    }

    // Count actions, not levels: removing a level's last action leaves the empty
    // level behind, so a template with no actions can still report a level.
    getTotalActionsCount(template) {
        return this.getAllActions(template).length;
    }

    // Deliberately separate from getTotalActionsCount: only these types can lack recipients.
    getNotifyActionsCount(template) {
        const recipientActions = this.getAllActions(template)
            .filter(action => RECIPIENT_ACTION_TYPES.includes(action.type));

        return recipientActions.length;
    }

    getRecipientsByEscalationLevel(template) {
        const escalationLevels = this.getResolvedEscalationLevels(template);
        if (!Array.isArray(escalationLevels)) {
            return [];
        }

        // Cache key includes the referenced template id so cache invalidates when the source switches
        const cacheKey = `${template.name}_${template.escalationTemplateId || ''}_${JSON.stringify(escalationLevels)}`;

        // Return cached result if available
        if (this.escalationLevelsCache.has(cacheKey)) {
            return this.escalationLevelsCache.get(cacheKey);
        }

        // Compute the result
        const result = escalationLevels.map((level, index) => {
            const contactGroups = new Set();
            const directRecipients = new Set();

            if (level.actions && Array.isArray(level.actions)) {
                level.actions.forEach(action => {
                    let actionContactGroups = [];
                    let actionRecipients = [];

                    // Handle different action types
                    if (action.type === 'EMAIL' || action.type === 'SMS') {
                        const actionObject = action[action.type.toLowerCase()];
                        if (actionObject && actionObject.contactGroups) {
                            actionContactGroups = actionObject.contactGroups;
                        }
                        if (actionObject && actionObject.recipients) {
                            actionRecipients = actionObject.recipients;
                        }
                    } else if (action.type === 'PHONE_CALL') {
                        if (action.phoneCall && action.phoneCall.contactGroups) {
                            actionContactGroups = action.phoneCall.contactGroups;
                        }
                        if (action.phoneCall && action.phoneCall.recipients) {
                            actionRecipients = action.phoneCall.recipients;
                        }
                    }

                    // Add contact groups to the set (automatically handles duplicates within this level)
                    actionContactGroups.forEach(groupName => {
                        if (groupName) {
                            contactGroups.add(groupName);
                        }
                    });

                    actionRecipients.forEach(recipient => {
                        if (recipient) {
                            directRecipients.add(recipient);
                        }
                    });
                });
            }

            return {
                levelIndex: index,
                displayName: level.displayName || `Level ${index + 1}`,
                contactGroups: Array.from(contactGroups),
                directRecipients: Array.from(directRecipients)
            };
        }).filter(level => level.contactGroups.length > 0 || level.directRecipients.length > 0);

        // Cache and return the result
        this.escalationLevelsCache.set(cacheKey, result);
        return result;
    }

    async loadAllProjectsAndCalculateCounts() {
        try {
            // Fetch all projects
            await this.fetchAllProjects();

            // Calculate counts for templates that have PROJECT_LABELS scope
            this.templates.forEach(template => {
                if (template.scope && template.scope.type === 'PROJECT_LABELS') {
                    this.calculateProjectCount(template);
                }
            });

            // Trigger UI update
            this.$scope.$applyAsync();
        } catch (error) {
            console.error('Error loading projects and calculating counts:', error); // eslint-disable-line no-console
        }
    }

    async fetchAllProjects() {
        const fetchPages = (pageToken = null, accumulatedProjects = []) => {
            const params = {
                organization: this.ProjectManager.currentProject.organization
            };
            if (pageToken) {
                params.pageToken = pageToken;
            }

            return this.IAMService.projects(params)
                .then(response => {
                    const allProjects = accumulatedProjects.concat(response.data);
                    if (response.nextPageToken) {
                        return fetchPages(response.nextPageToken, allProjects);
                    }
                    return allProjects;
                });
        };

        return fetchPages()
            .then(allProjects => {
                this.allProjects = allProjects;
            });
    }

    calculateProjectCount(template) {
        if (!template.scope || template.scope.type !== 'PROJECT_LABELS' || !template.scope.labels) {
            return;
        }

        const cacheKey = JSON.stringify(template.scope.labels);

        // Check cache first
        if (this.projectCountsCache.has(cacheKey)) {
            const cachedCount = this.projectCountsCache.get(cacheKey);
            template.matchingProjectsCount = cachedCount.matching;
            template.totalProjectsCount = cachedCount.total;
            return;
        }

        // Calculate matching projects
        const matchingProjects = this.allProjects.filter(project =>
            projectMatchesLabels(project, template.scope.labels)
        );

        const counts = {
            matching: matchingProjects.length,
            total: this.allProjects.length
        };

        // Cache the result
        this.projectCountsCache.set(cacheKey, counts);

        // Set on template for display
        template.matchingProjectsCount = counts.matching;
        template.totalProjectsCount = counts.total;
    }

    getScopeDisplayText(template) {
        if (!template.scope) return 'Not Set';

        if (template.scope.type === 'ALL_PROJECTS') {
            return 'All Projects';
        }

        if (template.scope.type === 'PROJECT_LABELS') {
            if (template.matchingProjectsCount !== undefined && template.totalProjectsCount !== undefined) {
                return `${template.matchingProjectsCount} of ${template.totalProjectsCount} projects`;
            }

            // Fallback while loading
            const labelCount = this.getScopeLabelCount(template);
            if (labelCount === 0) {
                return 'No labels';
            }
            return 'Calculating...';
        }

        return 'Not Set';
    }

    getTotalContactGroupsCount(template) {
        // Count the total number of contact group badges that would be displayed
        // This includes duplicates across different escalation levels
        let totalCount = 0;

        const escalationLevels = this.getRecipientsByEscalationLevel(template);
        escalationLevels.forEach(level => {
            totalCount += level.contactGroups.length;
        });

        return totalCount;
    }

    getTotalDirectRecipientsCount(template) {
        let totalCount = 0;

        const escalationLevels = this.getRecipientsByEscalationLevel(template);
        escalationLevels.forEach(level => {
            totalCount += level.directRecipients.length;
        });

        return totalCount;
    }

    positionTooltip(event) {
        // Find the tooltip element within the current container
        const tooltip = event.currentTarget.querySelector('.escalation-tooltip');
        if (tooltip) {
            const cursorX = event.clientX;
            const cursorY = event.clientY;

            // Position tooltip below cursor
            const tooltipRect = tooltip.getBoundingClientRect();
            tooltip.style.left = `${cursorX - tooltipRect.width}px`;
            tooltip.style.top = `${cursorY + 15}px`;

            // Adjust if tooltip goes off screen
            setTimeout(() => {
                const adjustedTooltipRect = tooltip.getBoundingClientRect();

                // Adjust horizontal position
                if (adjustedTooltipRect.right > window.innerWidth) {
                    tooltip.style.left = `${window.innerWidth - adjustedTooltipRect.width - 10}px`;
                } else if (adjustedTooltipRect.left < 0) {
                    tooltip.style.left = `10px`;
                }

                // Keep below cursor but adjust for bottom edge
                if (adjustedTooltipRect.bottom > window.innerHeight) {
                    const maxTop = window.innerHeight - adjustedTooltipRect.height - 10;
                    const minTop = cursorY + 15;
                    tooltip.style.top = `${Math.max(minTop, maxTop)}px`;
                }
            }, 0);
        }
    }

    async loadContactGroupWarnings() {
        // Collect all unique contact group resource names across all templates
        const allGroupNames = new Set();
        this.templates.forEach(template => {
            this.getAllContactGroups(template).forEach(name => allGroupNames.add(name));
        });

        if (allGroupNames.size === 0) {
            return;
        }

        this.loadingContactGroupInfo = true;

        try {
            const organizationName = this.ProjectManager.currentProject.organization;

            // Fetch contacts for each contact group in batches to avoid overwhelming the API
            const groups = Array.from(allGroupNames);
            const BATCH_SIZE = 5;
            for (let i = 0; i < groups.length; i += BATCH_SIZE) {
                const batch = groups.slice(i, i + BATCH_SIZE);
                // eslint-disable-next-line no-await-in-loop
                await Promise.all(batch.map(async (groupResourceName) => {
                    try {
                        const response = await this.IAMService.listContactsForOrganization({
                            organization: organizationName,
                            contactGroup: groupResourceName
                        });
                        const contacts = response.contacts || [];
                        this.contactGroupInfo.set(groupResourceName, buildContactGroupInfo(contacts));
                    } catch (error) {
                        console.error(`Failed to fetch contacts for group ${groupResourceName}:`, error); // eslint-disable-line no-console
                        // Don't show warnings for groups we couldn't check
                    }
                }));
            }

            // Create a new Map reference so AngularJS detects the change in child components
            this.contactGroupInfo = new Map(this.contactGroupInfo);
        } catch (error) {
            console.error('Failed to load contact group warnings:', error); // eslint-disable-line no-console
        } finally {
            this.loadingContactGroupInfo = false;
            this.$scope.$applyAsync();
        }
    }

    showValidationModal() {
        this.DialogService.show({
            template: validationModalTemplate,
            controller: ValidationModalController,
            controllerAs: '$ctrl',
            parent: document.body,
            clickOutsideToClose: false,
            escapeToClose: false,
            fullscreen: false
        });
    }
}
